import { useMemo } from 'react';
import type { FilterOption } from '@odo/shared-ui';
import { useVendor, useVendorList } from '../../../../hooks/useVendors';
import { SearchSelect } from '../../SearchSelect';
import { isMethodUsable } from '../../vendors/vendorDispatch';
import { CREATE_PO_FIELD_LABEL_CLASS } from './createPoFieldStyles';

interface SelectOrderDetailsStepProps {
    vendorId: string | null;
    orderMethodId: string | null;
    poNumber: string;
    sentAt: string;
    onVendorChange: (vendorId: string | null) => void;
    onOrderMethodChange: (orderMethodId: string | null) => void;
    onPoNumberChange: (value: string) => void;
    onSentAtChange: (value: string) => void;
}

export function SelectOrderDetailsStep({
    vendorId,
    orderMethodId,
    poNumber,
    sentAt,
    onVendorChange,
    onOrderMethodChange,
    onPoNumberChange,
    onSentAtChange
}: SelectOrderDetailsStepProps) {
    const { vendors, isLoading: isLoadingVendors } = useVendorList();
    const { vendor, isLoading: isLoadingVendor } = useVendor(vendorId);

    // Only vendors with an external ordering method: this route never sends an
    // email, so a vendor without one can't be recorded against.
    const vendorOptions = useMemo<FilterOption[]>(
        () =>
            vendors
                .filter((v) => v.orderMethods.external > 0)
                .map((v) => ({ value: v.id, label: v.name })),
        [vendors]
    );

    const externalMethods = useMemo(
        () => (vendor?.orderMethods ?? []).filter((m) => m.kind === 'external'),
        [vendor]
    );
    const usableExternalMethods = externalMethods.filter(isMethodUsable);
    const methodOptions = useMemo<FilterOption[]>(
        () => usableExternalMethods.map((m) => ({ value: m.id, label: m.name })),
        [usableExternalMethods]
    );

    return (
        <div className="flex flex-col gap-4">
            <div>
                <label className={CREATE_PO_FIELD_LABEL_CLASS}>Vendor *</label>
                <SearchSelect
                    value={vendorId ?? ''}
                    options={vendorOptions}
                    onChange={(value) => {
                        onVendorChange(value || null);
                        onOrderMethodChange(null);
                    }}
                    placeholder={isLoadingVendors ? 'Loading vendors…' : 'Select vendor…'}
                    searchPlaceholder="Search vendors…"
                    disabled={isLoadingVendors}
                />
            </div>

            <div>
                <label className={CREATE_PO_FIELD_LABEL_CLASS}>Ordering method *</label>
                <SearchSelect
                    value={orderMethodId ?? ''}
                    options={methodOptions}
                    onChange={(value) => onOrderMethodChange(value || null)}
                    placeholder={
                        !vendorId
                            ? 'Select a vendor first'
                            : isLoadingVendor
                              ? 'Loading methods…'
                              : 'Select ordering method…'
                    }
                    disabled={!vendorId || isLoadingVendor}
                />
                {vendorId && !isLoadingVendor && methodOptions.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">
                        This vendor has no usable external ordering method. Add one from the
                        vendor's page first.
                    </p>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={CREATE_PO_FIELD_LABEL_CLASS}>PO number</label>
                    <input
                        type="text"
                        value={poNumber}
                        onChange={(e) => onPoNumberChange(e.target.value)}
                        placeholder="Generated if left blank"
                        maxLength={120}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                </div>
                <div>
                    <label className={CREATE_PO_FIELD_LABEL_CLASS}>Ordered on *</label>
                    <input
                        type="datetime-local"
                        value={sentAt}
                        onChange={(e) => onSentAtChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                </div>
            </div>
        </div>
    );
}
